/**
 * Supplier draft-email (Phase 3). Thin SDK wrapper over the gmail-create-draft
 * Edge Function: it fills an email_templates row with order/patient data,
 * attaches the order's files plus an optional generated Excel prescription, and
 * creates a Gmail DRAFT (not a send) addressed to the supplier. Admin only.
 *
 * The Excel bytes come from getPrescriptionExcelBytes() in apps/web; this
 * wrapper base64-encodes them for transport (the edge function decodes std
 * base64).
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@insolech/db';
import { unwrapEdge, type EdgeEnvelope } from '../util/edge';

export interface CreateSupplierDraftInput {
  orderId: string;
  orderLineId: string;
  templateKey: string;
  language?: string;
  variables?: Record<string, string>;
  /** Generated prescription .xlsx bytes (from getPrescriptionExcelBytes). */
  excelBytes?: Uint8Array;
  excelFilename?: string;
}

export interface SupplierDraftResult {
  draft_id: string;
  message_id: string | null;
  to: string;
  account: string;
  attachment_count: number;
  /** Files omitted from the draft because the total exceeded Gmail's practical
   *  attachment cap. Reported (never silent) so the admin can attach them
   *  another way. */
  skipped_attachments?: { filename: string; size_mb: number }[];
}

/** Standard-base64 encode bytes, chunked to avoid a call-stack overflow. */
function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

export async function createSupplierDraft(
  client: SupabaseClient<Database>,
  input: CreateSupplierDraftInput,
): Promise<SupplierDraftResult> {
  const { data, error } = await client.functions.invoke<unknown>('gmail-create-draft', {
    body: {
      order_id: input.orderId,
      order_line_id: input.orderLineId,
      template_key: input.templateKey,
      language: input.language,
      variables: input.variables ?? {},
      excel_base64: input.excelBytes ? bytesToBase64(input.excelBytes) : undefined,
      excel_filename: input.excelFilename,
    },
  });
  if (error) {
    // supabase-js reports a non-2xx as a generic FunctionsHttpError; surface the
    // function's own {error:{code,message}} envelope from the response body so
    // the UI can show the real reason (e.g. "No provider assigned to this line").
    const ctx = (error as { context?: Response }).context;
    const body =
      ctx && typeof ctx.json === 'function'
        ? ((await ctx.json().catch(() => null)) as EdgeEnvelope<SupplierDraftResult> | null)
        : null;
    if (body?.error) {
      const e = new Error(body.error.message) as Error & { code?: string };
      e.code = body.error.code;
      throw e;
    }
    throw error;
  }
  return unwrapEdge<SupplierDraftResult>(data);
}
